import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useNotification, useUserContext } from '@app/providers';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from 'components/ui/tabs';
import {
  OkedSelectTrigger,
  StyledOkedListBlock,
  TextField,
} from '@app/components';
import { ProfileAvatar } from '../../components';
import { IconCheckCircle16 } from '@app/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { Company, Oked } from '@app/models';
import * as yup from 'yup';
import {
  getCompanyByBin,
  updateCompany,
  uploadCompanyAvatarFiles,
} from '@app/api';
import { CompanyInfoData, FormCompanyInfo, useForm } from '@app/forms';
import { LayoutProfile } from '@app/layouts';
import { useTranslation } from 'react-i18next';

interface Props {}

interface CompanyData extends CompanyInfoData {
  okeds: Oked[];
  additionalInformation: string;
}

function PageProfileCompanyEdit(props: Props) {
  const { bin } = useParams();
  const {
    setCompany: setCompanyStore,
    setCurrentProfile,
    currentProfile,
    user: contextUser,
  } = useUserContext();
  const { showNotification } = useNotification();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pending, setPending] = useState<boolean>(false);
  const [company, setCompany] = useState<Company | null>(null);

  const { onChange, values, errors, validate, setValues } =
    useForm<CompanyData>({
      legalAddress: '',
      address: '',
      phones: [],
      instagram: '',
      telegram: '',
      webSite: '',
      whatsAppNumber: '',
      additionalInformation: '',
      kato: null,
      okeds: [],
      email: '',
      filesIds: null,
      avatarFilesIds: null,
    });

  const getData = useCallback(async () => {
    try {
      const response = await getCompanyByBin(bin as string);

      setValues({
        legalAddress: response.legalAddress || '',
        address: response.address || '',
        phones: !!response.phones ? JSON.parse(response.phones) : [],
        instagram: response.instagram || '',
        telegram: response.telegram || '',
        webSite: response.webSite || '',
        whatsAppNumber: response.whatsAppNumber || '',
        additionalInformation: response.additionalInformation || '',
        kato: response.kato || null,
        okeds: response.okeds || [],
        email: response.email || '',
        filesIds: response.filesIds || null,
        avatarFilesIds: response.avatarFilesIds || null,
      });
      setCompany(response);
      setPending(false);
    } catch (e) {
      setPending(false);
    }
  }, [bin, setValues]);

  const onClickCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const onClickBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const onChangeOked = useCallback(
    (data: { value: Oked }) => {
      onChange([data.value, ...values.okeds], 'okeds');
    },
    [onChange, values.okeds]
  );

  const onClearOked = useCallback(
    (number: number) => {
      onChange(
        values.okeds.filter((val, index) => index !== number),
        'okeds'
      );
    },
    [onChange, values.okeds]
  );

  const onChangeAvatar = useCallback(
    async (files: File) => {
      try {
        const response = await uploadCompanyAvatarFiles(company!.id, [files]);

        // Update form values
        setValues({
          filesIds: response.filesIds,
          avatarFilesIds: response.avatarFilesIds,
        });

        // Update local company state
        setCompany((prev) =>
          prev
            ? {
                ...prev,
                filesIds: response.filesIds,
                avatarFilesIds: response.avatarFilesIds,
              }
            : null
        );

        // Refetch company data to ensure we have latest data
        const updatedCompany = await getCompanyByBin(company!.bin);

        // Update company store in context
        setCompanyStore(updatedCompany);

        // Update current profile if this is the active company
        if (
          currentProfile.currentType === 'company' &&
          currentProfile.id === company!.id
        ) {
          setCurrentProfile({
            ...updatedCompany,
            currentType: 'company',
          });
        }

        // Also update the user's companies array
        if (contextUser.companies) {
          const updatedCompanies = contextUser.companies.map((c) =>
            c.id === company!.id ? updatedCompany : c
          );
          // This will trigger a re-render in components that depend on user.companies
          contextUser.companies = updatedCompanies;
        }

        showNotification({
          variant: 'success',
          message: 'Аватар успешно обновлен',
        });
      } catch (e) {
        showNotification({
          variant: 'error',
          message: 'Не удалось обновить аватар',
        });
      }
    },
    [
      company,
      setValues,
      setCompanyStore,
      currentProfile,
      setCurrentProfile,
      contextUser,
      showNotification,
    ]
  );

  const onClickSave = useCallback(async () => {
    try {
      const hasErrors = await validate(
        yup.object().shape({
          legalAddress: yup
            .string()
            .required(t('required', { ns: 'validation' })!),
          address: yup.string().required(t('required', { ns: 'validation' })!),
          kato: yup
            .object()
            .nullable()
            .required(t('required', { ns: 'validation' })!),
          email: yup.string().required(t('required', { ns: 'validation' })!),
        })
      );

      if (hasErrors) {
        return;
      }

      setPending(true);

      const toUpdateData: Partial<Company> = {
        legalAddress: values.legalAddress,
        address: values.address,
        katoCode: values.kato!.code,
        okeds: values.okeds,
        email: values.email,
      };

      if (!!values.instagram) {
        toUpdateData.instagram = values.instagram;
      }

      if (!!values.telegram) {
        toUpdateData.telegram = values.telegram;
      }

      if (!!values.webSite) {
        toUpdateData.webSite = values.webSite;
      }

      if (!!values.whatsAppNumber) {
        toUpdateData.whatsAppNumber = values.whatsAppNumber;
      }

      if (!!values.additionalInformation) {
        toUpdateData.additionalInformation = values.additionalInformation;
      }

      if (values.phones.length > 0) {
        toUpdateData.phones = JSON.stringify(values.phones);
      }

      const response = await updateCompany(company!.id, toUpdateData);

      // Refetch company data to ensure we have all the latest data
      const updatedCompany = await getCompanyByBin(company!.bin);

      // Update company store in context
      setCompanyStore(updatedCompany);

      // Update current profile if this is the active company
      if (
        currentProfile.currentType === 'company' &&
        currentProfile.id === company!.id
      ) {
        setCurrentProfile({
          ...updatedCompany,
          currentType: 'company',
        });
      }

      // Update the user's companies array
      if (contextUser.companies) {
        const updatedCompanies = contextUser.companies.map((c) =>
          c.id === company!.id ? updatedCompany : c
        );
        contextUser.companies = updatedCompanies;
      }

      showNotification({
        variant: 'success',
        message: 'Данные компании успешно обновлены',
      });

      setPending(false);

      navigate(`/profile/company/${company!.bin}`);
    } catch (e) {
      showNotification({
        variant: 'error',
        message: 'Не удалось обновить данные компании',
      });

      setPending(false);
    }
  }, [
    errors,
    company,
    navigate,
    setCompanyStore,
    showNotification,
    validate,
    values,
    t,
  ]);

  const leftContent = useMemo(
    () => (
      <div className="flex flex-col items-center w-full">
        <ProfileAvatar
          avatarFiles={
            values?.avatarFilesIds ? values.avatarFilesIds.split(',')[0] : ''
          }
          onSubmit={onChangeAvatar}
          editable={true}
          className="mx-auto"
        />
      </div>
    ),
    [onChangeAvatar, values.avatarFilesIds]
  );

  const rightContent = useMemo(() => {
    if (!company) {
      return null;
    }

    return (
      <div className="w-full">
        <FormCompanyInfo
          company={company}
          values={values}
          onChange={onChange}
          errors={errors}
        />
      </div>
    );
  }, [company, errors, onChange, values]);

  useEffect(() => {
    getData();
  }, []);

  return (
    <LayoutProfile
      pending={pending}
      toolbarProps={{
        onClickBack,
        title: 'Данные организации',
      }}
      leftContent={leftContent}
      rightContent={rightContent}
    >
      <Card className="w-full">
        <CardContent className="p-4 sm:p-6 px-2 sm:px-6">
          <Tabs defaultValue="0" className="w-full">
            <TabsList className="grid w-full grid-cols-1 h-auto p-1 mb-6">
              <TabsTrigger
                value="0"
                className="text-sm py-3 px-4 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                Профессиональные данные
              </TabsTrigger>
            </TabsList>
            <TabsContent value="0" className="mt-0">
              <div className="space-y-8 px-1 sm:px-0">
                <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  <div className="sm:col-span-2 lg:col-span-3 xl:col-span-4">
                    <OkedSelectTrigger
                      label={'Категория'}
                      placeholder="Выберите категорию"
                      value={
                        values.okeds && values.okeds.length > 0
                          ? values.okeds[0].nameRu
                          : undefined
                      }
                      onChange={onChangeOked}
                      helperText={errors.okeds || undefined}
                      error={!!errors.okeds}
                    />
                  </div>
                  <div className="sm:col-span-2 lg:col-span-3 xl:col-span-4">
                    <StyledOkedListBlock
                      list={values.okeds}
                      onClickClear={onClearOked}
                    />
                  </div>
                </div>
                <div className="w-full">
                  <TextField
                    label="Дополнительная информация"
                    onChange={onChange}
                    name="additionalInformation"
                    value={values.additionalInformation}
                    placeholder="Дополнительная информация"
                    helperText={errors.additionalInformation}
                    error={!!errors.additionalInformation}
                    className="w-full"
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex flex-col sm:flex-row justify-end gap-4 mt-8 pt-6 border-t">
            <Button
              variant="outline"
              onClick={onClickCancel}
              className="w-full sm:w-auto order-2 sm:order-1"
            >
              Отмена
            </Button>
            <Button
              onClick={onClickSave}
              disabled={pending}
              className="w-full sm:w-auto order-1 sm:order-2"
            >
              <IconCheckCircle16 className="mr-2 h-4 w-4" />
              Сохранить
            </Button>
          </div>
        </CardContent>
      </Card>
    </LayoutProfile>
  );
}

export default memo(PageProfileCompanyEdit);
